import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { Cta } from '../Cta';
import { Reveal } from '../Reveal';
import { useScore } from '../ScoreContext';
const earlyStages = ['Helps your readers apply your ideas to their situation', 'Gives them a useful next move', 'Shows you what they need'];

/**
 * Path Author Leverage builds. Final stage is the endpoint; services are examples.
 */
function ReaderToWorkSpine() {
  const reduceMotion = useReducedMotion();
  return <div className="relative mx-auto w-fit max-w-full text-left lg:mx-0 lg:w-full lg:max-w-none" data-id="element-827" data-mp="1sem7tqoc4wyc">
      <p className="text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-text-muted" data-id="element-828" data-mp="1xc5mgroy3bqt">
        We build the path that
      </p>

      {/* Line terminates at the final stage marker — not beside the examples */}
      <div className="relative mt-4 lg:mt-5" data-id="element-829" data-mp="14o0060i8teov">
        <div className="absolute bottom-0 left-[0.4375rem] top-[0.7rem] w-px bg-gradient-to-b from-seaglass from-[65%] to-sand" aria-hidden="true" data-id="element-830" data-mp="19mtcvqpph1nz" />

        <ol className="relative space-y-4 lg:space-y-5" data-id="element-831" data-mp="10pwjetlzhikv">
          {earlyStages.map((label, i) => <motion.li key={label} className="grid grid-cols-[0.875rem_1fr] items-start gap-x-4" initial={reduceMotion ? false : {
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: reduceMotion ? 0 : 0.1 + i * 0.08,
          duration: 0.35,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-832" data-mp="vs3gc9fz94ot" data-mpi={i}>
              <span className="relative z-10 mt-[0.55rem] h-2 w-2 justify-self-center rounded-full bg-seaglass ring-[3px] ring-paper" aria-hidden="true" data-id="element-833" data-mp="d027ce9hny1o" />
              <span className="font-serif text-lg leading-snug text-navy md:text-xl" data-id="element-834" data-mp="83tvb9ql8kyq">
                {label}
              </span>
            </motion.li>)}
        </ol>

        <motion.div className="relative mt-4 grid grid-cols-[0.875rem_1fr] items-start gap-x-4 lg:mt-5" initial={reduceMotion ? false : {
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: reduceMotion ? 0 : 0.38,
        duration: 0.4,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-835" data-mp="22fi6r1xm25ed">
          <span className="relative z-10 mt-[0.6rem] h-2.5 w-2.5 justify-self-center rounded-full bg-sand ring-[3px] ring-paper" aria-hidden="true" data-id="element-836" data-mp="3hzhptbim4zb1" />
          <div className="min-w-0" data-id="element-837" data-mp="3myc6o7x0sdsi">
            <p className="font-serif text-xl font-semibold leading-snug text-navy md:text-[1.35rem]" data-id="element-838" data-mp="eiiozpqycw9v">
              Connects them with the right way to work with you:
            </p>
            <p className="mt-2.5 text-sm font-medium leading-[1.45] text-[#c89860] md:text-base" data-id="element-839" data-mp="jezn9crv8r0o">
              Coaching · Consulting · Speaking · Training · Workshops
              <br data-id="element-840" data-mp="9hl9xm18oicl" />
              Courses · Certification · Communities · and more
            </p>
          </div>
        </motion.div>
      </div>
    </div>;
}
function ScrollCue() {
  const reduceMotion = useReducedMotion();
  return <motion.div className="hero-scroll-cue mt-10 flex justify-center md:mt-12" aria-hidden="true" animate={reduceMotion ? undefined : {
    y: [0, 4, 0]
  }} transition={reduceMotion ? undefined : {
    duration: 2.2,
    repeat: Infinity,
    ease: 'easeInOut'
  }} data-id="element-841" data-mp="2u7295zefqkqe">
      <ChevronDown className="h-5 w-5 text-seaglass/50" strokeWidth={1.75} data-id="element-842" data-mp="2wnjv2a2bd52h" />
    </motion.div>;
}
function ScoreCta({
  openDiagnostic,
  align
}: {
  openDiagnostic: () => void;
  align: 'center' | 'start';
}) {
  return <div className={`flex w-full flex-col gap-2.5 ${align === 'center' ? 'items-center' : 'items-start'} sm:w-fit`} data-id="element-843" data-mp="23zo3tkvik5d3">
      <Cta variant="primary" size="lg" onClick={openDiagnostic} className="w-full max-w-sm px-6 py-3.5 text-base sm:w-auto sm:px-8 sm:py-4 sm:text-lg sm:whitespace-nowrap" data-id="element-844" data-mp="1yygv9jljr25d">
        Get Your Reader-to-Revenue Score
      </Cta>
      <p className={`text-sm leading-snug text-navy/60 ${align === 'center' ? 'text-center' : 'text-left'}`} data-id="element-845" data-mp="1l8da6vyelehi">
        <span className="block" data-id="element-846" data-mp="1galk87hbk2fy">8 questions to see how clearly</span>
        <span className="block" data-id="element-847" data-mp="jif65yldk0dp">reader interest connects to your business.</span>
      </p>
    </div>;
}
export function HeroV2() {
  const {
    openDiagnostic
  } = useScore();
  return <section className="hero-v2 relative z-10 bg-paper px-6 py-14 shadow-[0_14px_28px_-22px_rgba(26,40,56,0.32)] md:px-10 md:py-20" data-id="element-848" data-mp="nhxkgbioxd7k">
      <div className="mx-auto max-w-6xl" data-id="element-849" data-mp="xad7hn8ugrzb">
        <div className="mx-auto max-w-3xl text-center" data-id="element-850" data-mp="xbekvydc1qk7">
          <Reveal data-id="element-851" data-mp="129g0uafgp9rg">
            <span className="inline-block max-w-full rounded-full border border-navy/12 bg-white px-3 py-1.5 text-center text-[0.875rem] font-medium leading-snug text-navy/90 shadow-sm sm:px-4 sm:text-[0.9375rem] md:text-base" data-id="element-852" data-mp="127v7up8spyvp">
              For expert‑authors whose work goes beyond the book
            </span>
          </Reveal>
          <Reveal delay={0.04} data-id="element-853" data-mp="1g7weymfnm2jm">
            <h1 className="hero-v2-title mt-5 font-serif text-[clamp(1.7rem,5vw,4rem)] leading-[1.08] tracking-[-0.02em] text-navy" data-id="element-854" data-mp="3d17pvvinqqpf">
              <span className="block" data-id="element-855" data-mp="3i13u9vv9ok5i">You wrote the&nbsp;book.</span>
              <span className="mt-1.5 block italic text-[#2f4a42]" data-id="element-856" data-mp="3ag9tglvqtimt">
                We build what
                <br className="min-[480px]:hidden" data-id="element-857" data-mp="35q342jtepu3i" /> comes&nbsp;next.
              </span>
            </h1>
          </Reveal>

          {/* Mobile / tablet: CTA in the first viewport, directly under the H1 */}
          <Reveal delay={0.08} className="hero-cta-mobile mt-8 flex justify-center lg:hidden" data-id="element-858" data-mp="2vs5g7xfglzbf">
            <ScoreCta openDiagnostic={openDiagnostic} align="center" data-id="element-859" data-mp="3tfxfcwy2valn" />
          </Reveal>
        </div>

        <div className="hero-v2-body mx-auto mt-10 max-w-5xl border-t border-gray-200/70 pt-9 md:mt-12 md:pt-12" data-id="element-860" data-mp="3ofsvcwxynxqo">
          <div className="grid gap-9 md:gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-center lg:gap-12 xl:gap-14" data-id="element-861" data-mp="3jg6mt0t2v5o7">
            <div className="flex flex-col gap-5 text-center lg:text-left" data-id="element-862" data-mp="3feuswj367ya7">
              <Reveal delay={0.08} data-id="element-863" data-mp="282b63nmiyouv">
                <p className="mx-auto max-w-xl text-lg font-semibold leading-snug text-navy md:text-xl lg:mx-0" data-id="element-864" data-mp="283dwlufxqpa9">
                  Your book gives readers the ideas.
                </p>
                <p className="mx-auto mt-2.5 max-w-md text-pretty text-base leading-relaxed text-text-muted md:max-w-xl md:text-lg lg:mx-0" data-id="element-865" data-mp="25ixydmz8u5i7">
                  We turn those ideas into a path your readers can{' '}
                  <span className="whitespace-nowrap" data-id="element-866" data-mp="1vj767ry5o2la">use—and</span> you can
                  keep using across your business.
                </p>
              </Reveal>

              {/* Desktop: CTA stays in the left conversion column */}
              <Reveal delay={0.12} className="hidden lg:flex lg:justify-start" data-id="element-867" data-mp="1rhlmpnr48q24">
                <ScoreCta openDiagnostic={openDiagnostic} align="start" data-id="element-868" data-mp="1rjonn2tt43lm" />
              </Reveal>
            </div>

            <Reveal delay={0.1} className="hero-path flex w-full justify-center lg:justify-start lg:self-center" data-id="element-869" data-mp="1hrsmtk0c5zdp">
              <ReaderToWorkSpine data-id="element-870" data-mp="13rrc7cu9rdl1" />
            </Reveal>
          </div>

          <ScrollCue data-id="element-871" data-mp="13unaj2a144a0" />
        </div>
      </div>
    </section>;
}